import { Link } from 'react-router'
import type { DiscoverOrg } from '../api/discover'
import styles from './OrgCard.module.css'

/** Internal org types read badly in a directory; these are what people call them. */
const TYPE_LABEL: Record<string, string> = {
  organization: 'Club',
  league: 'League',
  university: 'University',
}

export function OrgCard({ org }: { org: DiscoverOrg }) {
  const typeLabel = TYPE_LABEL[org.type] ?? org.type
  // Leagues are measured by the competitions they run; clubs and universities
  // by the people in them. Showing "0 teams" on a league nobody has entered yet
  // is worse than showing what it does have.
  const isLeague = org.type === 'league'

  return (
    <Link to={`/org/${org.slug}`} className={styles.card}>
      <div className={styles.head}>
        <div className={styles.logoWrap}>
          <div className={styles.logo}>
            {org.logo_url
              ? <img src={org.logo_url} alt="" />
              : <span>{org.name.charAt(0)}</span>}
          </div>
          {/* The one place an org's colour lands. See the discovery visual rules. */}
          {org.primary_color && (
            <span
              className={styles.brandRule}
              style={{ background: org.primary_color }}
              aria-hidden="true"
            />
          )}
        </div>
        <span className={styles.type}>{typeLabel}</span>
      </div>

      <h3 className={styles.name}>{org.name}</h3>
      {org.university_name && org.university_name !== org.name && (
        <p className={styles.sub}>{org.university_name}</p>
      )}

      {org.description && <p className={styles.description}>{org.description}</p>}

      <div className={styles.footer}>
        <span className={styles.stat}>
          <strong>{org.member_count}</strong> {org.member_count === 1 ? 'member' : 'members'}
        </span>
        {org.games.length > 0 && (
          <span className={styles.stat}>
            {org.games.length} {org.games.length === 1 ? 'game' : 'games'}
          </span>
        )}
        <span className={styles.spacer} />
        {isLeague && org.registration_open && <span className={styles.status}>Registration open</span>}
        {!isLeague && org.recruiting_open && <span className={styles.status}>Recruiting</span>}
      </div>
    </Link>
  )
}
